import { SingleAndMultiSelectFieldOption } from 'module/api/field/types';
import { AddOptionsInput as AddOptionsInputElement } from 'module/ui/components/atoms/choices/add-option';
import { SortableOptions } from 'module/ui/components/atoms/choices/list';
import { DrawerBreadcrumb } from 'module/ui/components/atoms/drawer/custom-layout-drawer';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { getMaximumValueForOption } from 'components/organisms/fields/utils';
import { ManageOptionInstructionsDrawer } from './manage-option-instruction-drawer';
import { useManageOptionInstructionDrawerState } from './manage-option-instruction-state';

const AddOptionsInput = styled(AddOptionsInputElement)`
  .ant-input {
    border-radius: var(--border-radius-default);
  }
`;
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${AddOptionsInput} {
    margin-top: var(--spacing-03);
    margin-left: 24px;
    margin-right: var(--height-base);
  }
`;

interface OptionListProps {
  value?: SingleAndMultiSelectFieldOption[];
  onChange?: (options: SingleAndMultiSelectFieldOption[]) => void;
  manageOptionInstructionDrawerProps: {
    title: ReactNode;
    breadcrumbs: DrawerBreadcrumb[];
  };
  addedAsFormItem?: boolean;
  className?: string;
}

export const OptionList: FC<OptionListProps> = ({
  value: options = [],
  onChange,
  manageOptionInstructionDrawerProps,
  className,
  addedAsFormItem,
}) => {
  const {
    manageOptionInstructionDrawerState,
    openManageOptionInstructionDrawer,
    closeManageOptionInstructionDrawer,
  } = useManageOptionInstructionDrawerState();
  const breadcrumbsForManageOptionInstructionDrawer = useMemo(
    () =>
      manageOptionInstructionDrawerState.visible
        ? manageOptionInstructionDrawerProps.breadcrumbs.concat({
            title: manageOptionInstructionDrawerState.option.label,
          })
        : manageOptionInstructionDrawerProps.breadcrumbs,
    [
      manageOptionInstructionDrawerProps.breadcrumbs,
      manageOptionInstructionDrawerState,
    ],
  );
  const addOption = useCallback(
    (option) => {
      const newOptions = options.concat({
        ...option,
        value: getMaximumValueForOption(options) + 1,
        position: options.length + 1,
      });

      onChange?.(newOptions);
    },
    [onChange, options],
  );
  const removeOption = useCallback(
    (option) => {
      const newOptions = options.filter((x) => x.value !== option.value);

      onChange?.(newOptions.map((x, index) => ({ ...x, position: index + 1 })));
    },
    [onChange, options],
  );
  const updateOptions = useCallback(
    (options) => {
      onChange?.(options);
    },
    [onChange],
  );
  const onOptionWithInstructionChange = useCallback(
    (option: any) => {
      const newOptions = options.map((x) =>
        x.value === option.value ? { ...x, ...option } : x,
      );

      onChange?.(newOptions);
      closeManageOptionInstructionDrawer();
    },
    [closeManageOptionInstructionDrawer, onChange, options],
  );

  return (
    <Wrapper className={className}>
      <SortableOptions
        optionsManagedByAi={true}
        options={options}
        removeOption={removeOption}
        updateOptions={updateOptions}
        editOption={openManageOptionInstructionDrawer}
      />
      <AddOptionsInput
        existingOptions={options}
        addOption={addOption}
        addedAsFormItem={addedAsFormItem}
      />
      <ManageOptionInstructionsDrawer
        visible={manageOptionInstructionDrawerState.visible}
        option={
          manageOptionInstructionDrawerState.visible
            ? manageOptionInstructionDrawerState.option
            : undefined
        }
        breadcrumbs={breadcrumbsForManageOptionInstructionDrawer}
        onClose={closeManageOptionInstructionDrawer}
        title={manageOptionInstructionDrawerProps.title}
        onSubmit={onOptionWithInstructionChange}
      />
    </Wrapper>
  );
};
